import React, { useState, useMemo, useRef } from "react";
import {
  Menu,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import PopupState, { bindMenu } from "material-ui-popup-state";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/CheckBoxRounded";
import SearchIcon from "@mui/icons-material/Search";
import "./SSOOrgSelector.css";

interface SSOOrg {
  dpId: string;
  dpName: string;
  idpType: string;
  parentDpId?: string;
}

interface SSOOrgNode extends SSOOrg {
  _children: SSOOrgNode[];
}

interface Props {
  organizations: SSOOrg[];
  selectedOrg: SSOOrg | null;
  onSelect: (org: SSOOrg) => void;
  onOpen: () => void;
  loading: boolean;
  placeholder?: string;
  locked?: boolean;
}

function buildTree(orgs: SSOOrg[]): SSOOrgNode[] {
  const byId: Record<string, SSOOrgNode> = {};
  orgs.forEach((o) => {
    byId[o.dpId] = { ...o, _children: [] };
  });

  const roots: SSOOrgNode[] = [];
  orgs.forEach((o) => {
    const node = byId[o.dpId];
    if (o.parentDpId && byId[o.parentDpId]) {
      byId[o.parentDpId]._children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRecursive = (nodes: SSOOrgNode[]) => {
    nodes.sort((a, b) => a.dpName.localeCompare(b.dpName));
    nodes.forEach((n) => sortRecursive(n._children));
  };
  sortRecursive(roots);

  return roots;
}

function filterTree(nodes: SSOOrgNode[], search: string): SSOOrgNode[] {
  if (!search) return nodes;
  const term = search.toLowerCase();
  const result: SSOOrgNode[] = [];
  for (const node of nodes) {
    const matchedChildren = filterTree(node._children, search);
    if (node.dpName.toLowerCase().includes(term) || matchedChildren.length > 0) {
      result.push({ ...node, _children: matchedChildren });
    }
  }
  return result;
}

const SSOOrgSelector: React.FC<Props> = ({
  organizations,
  selectedOrg,
  onSelect,
  onOpen,
  loading,
  placeholder = "Select organization",
  locked = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const triggerRef = useRef<HTMLDivElement>(null);

  const tree = useMemo(() => buildTree(organizations || []), [organizations]);
  const visibleTree = useMemo(
    () => filterTree(tree, searchValue),
    [tree, searchValue]
  );

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelect = (org: SSOOrgNode, popupState: any) => {
    onSelect({
      dpId: org.dpId,
      dpName: org.dpName,
      idpType: org.idpType,
      parentDpId: org.parentDpId,
    });
    popupState.close();
  };

  const renderNodes = (
    nodes: SSOOrgNode[],
    popupState: any,
    level: number = 0,
    keyPrefix: string = ""
  ): React.ReactNode[] => {
    const list: React.ReactNode[] = [];
    nodes.forEach((node, i) => {
      const key = `${keyPrefix}${i}`;
      const hasChildren = node._children.length > 0;
      const isExpanded = searchValue ? true : expanded[key] || false;
      const isSelected = selectedOrg?.dpId === node.dpId;

      list.push(
        <div key={key} className="sso-org-node">
          <MenuItem
            className="sso-org-item"
            style={{ paddingLeft: `${12 + level * 16}px` }}
          >
            <span
              className="sso-org-toggle"
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) toggleExpand(key);
              }}
            >
              {hasChildren ? (isExpanded ? "−" : "+") : ""}
            </span>
            <ListItemText
              primary={node.dpName}
              className="sso-org-name"
              onClick={() => handleSelect(node, popupState)}
            />
            {isSelected && <CheckIcon className="sso-org-check" />}
          </MenuItem>
          {hasChildren && isExpanded && (
            <div className="sso-org-children">
              {renderNodes(node._children, popupState, level + 1, key + "_")}
            </div>
          )}
        </div>
      );
    });
    return list;
  };

  return (
    <PopupState variant="popover" popupId="sso-org-popup">
      {(popupState) => (
        <>
          <TextField
            id="sso-org-trigger"
            label="Organization"
            variant="outlined"
            placeholder={placeholder}
            size="small"
            fullWidth
            disabled={locked}
            value={selectedOrg ? selectedOrg.dpName : ""}
            ref={triggerRef}
            onClick={() => {
              if (locked) return;
              if (triggerRef.current) {
                popupState.open(triggerRef.current);
                onOpen();
              }
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              className: "labelTextbox sso-org-trigger-input",
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon
                    className="sso-org-trigger-arrow"
                    style={{
                      transform: popupState.isOpen ? "rotate(-180deg)" : "none",
                      visibility: locked ? "hidden" : "visible",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Menu
            {...bindMenu(popupState)}
            className="sso-org-menu"
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClose={() => {
              popupState.close();
              setSearchValue("");
            }}
          >
            <MenuItem
              className="sso-org-search-item"
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search organization..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </MenuItem>
            {loading && (
              <MenuItem disabled className="sso-org-empty">
                Loading organizations...
              </MenuItem>
            )}
            {!loading && visibleTree.length === 0 && (
              <MenuItem disabled className="sso-org-empty">
                {searchValue
                  ? "No matching organizations"
                  : "No organizations available"}
              </MenuItem>
            )}
            {!loading && visibleTree.length > 0 && (
              <div className="sso-org-tree">
                {renderNodes(visibleTree, popupState)}
              </div>
            )}
          </Menu>
        </>
      )}
    </PopupState>
  );
};

export default SSOOrgSelector;
